import React, { useState } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { uploadWikiAttachment, deleteWikiAttachment } from '../../../services/wikiService';
import { wikiTheme } from '../wikiTheme';

/**
 * Component for managing document attachments on wiki entries
 * @param {string} entryId - The wiki entry ID
 * @param {array} attachments - Current attachments array
 * @param {function} onAttachmentsChange - Callback when attachments change
 * @param {boolean} readOnly - Whether to show in read-only mode
 */
const AttachmentManager = ({ entryId, attachments = [], onAttachmentsChange, readOnly = false }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📽️';
    if (fileType.includes('text')) return '📃';
    return '📎';
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError(null);
    setUploading(true);

    try {
      const attachmentData = await uploadWikiAttachment(entryId, file);
      // Note: uploadWikiAttachment now saves to DB, so we need to notify parent to reload
      const updatedAttachments = [...attachments, attachmentData];
      onAttachmentsChange(updatedAttachments);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDelete = async (storagePath) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    setError(null);
    try {
      await deleteWikiAttachment(entryId, storagePath);
      const updatedAttachments = attachments.filter(att => att.storagePath !== storagePath);
      onAttachmentsChange(updatedAttachments);
    } catch (err) {
      setError('Failed to delete attachment: ' + err.message);
    }
  };

  const handleDownload = (url, name) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Accordion 
      defaultExpanded={attachments && attachments.length > 0}
      sx={{
        backgroundColor: wikiTheme.dark,
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: wikiTheme.primary }} />}
        sx={{
          backgroundColor: wikiTheme.mediumGray,
          borderBottom: `2px solid ${wikiTheme.primary}`,
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
          },
        }}
      >
        <Typography 
          sx={{ 
            color: wikiTheme.primary, 
            fontFamily: wikiTheme.font,
            fontWeight: 'bold',
            fontSize: '1.1rem',
          }}
        >
          📎 Attachments
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, backgroundColor: wikiTheme.dark }}>
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)} 
            sx={{ 
              mb: 2,
              backgroundColor: wikiTheme.mediumGray,
              color: wikiTheme.secondary,
              '& .MuiAlert-icon': {
                color: '#ff6b6b'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {attachments && attachments.length > 0 && (
          <List dense sx={{ mb: 2 }}>
            {attachments.map((attachment, index) => (
              <ListItem
                key={index}
                sx={{
                  border: `1px solid ${wikiTheme.primary}`,
                  borderLeft: `3px solid ${wikiTheme.primary}`,
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: wikiTheme.mediumGray,
                  '&:hover': {
                    bgcolor: '#3d3d3d',
                    transform: 'translateX(2px)',
                    transition: 'all 0.2s',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  <Typography sx={{ fontSize: '1.5rem' }}>
                    {getFileIcon(attachment.type)}
                  </Typography>
                </Box>
                <ListItemText
                  primary={
                    <Typography 
                      sx={{ 
                        color: wikiTheme.secondary,
                        fontFamily: wikiTheme.font,
                        fontWeight: 'bold'
                      }}
                    >
                      {attachment.name}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                      <Chip 
                        label={formatFileSize(attachment.size)} 
                        size="small" 
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem',
                          backgroundColor: wikiTheme.primary,
                          color: wikiTheme.dark,
                          fontFamily: wikiTheme.font,
                          fontWeight: 'bold'
                        }}
                      />
                      {attachment.uploadedAt && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: wikiTheme.tertiary,
                            fontFamily: wikiTheme.font
                          }}
                        >
                          {new Date(attachment.uploadedAt.toDate()).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleDownload(attachment.url, attachment.name)}
                    size="small"
                    sx={{ 
                      mr: 1,
                      color: wikiTheme.primary,
                      '&:hover': {
                        backgroundColor: 'rgba(217, 141, 42, 0.1)',
                      }
                    }}
                    title="Download"
                  >
                    <DownloadIcon />
                  </IconButton>
                  {!readOnly && (
                    <IconButton
                      edge="end"
                      onClick={() => handleDelete(attachment.storagePath)}
                      size="small"
                      sx={{
                        color: '#ff6b6b',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        }
                      }}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {!readOnly && (
          <Box>
            <input
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
              style={{ display: 'none' }}
              id={`attachment-upload-${entryId}`}
              type="file"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <label htmlFor={`attachment-upload-${entryId}`}>
              <Button
                variant="contained"
                component="span"
                startIcon={uploading ? <CircularProgress size={20} sx={{ color: wikiTheme.dark }} /> : <AttachFileIcon />}
                disabled={uploading}
                size="small"
                sx={{
                  backgroundColor: wikiTheme.primary,
                  color: wikiTheme.dark,
                  fontFamily: wikiTheme.font,
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#e0a23d',
                  },
                  '&:disabled': {
                    backgroundColor: wikiTheme.lightGray,
                    color: wikiTheme.tertiary,
                  }
                }}
              >
                {uploading ? 'Uploading...' : 'Add Attachment'}
              </Button>
            </label>
            <Typography 
              variant="caption" 
              display="block" 
              sx={{ 
                mt: 1, 
                color: wikiTheme.tertiary,
                fontFamily: wikiTheme.font
              }}
            >
              Max 25MB. Supported: PDF, Word, Excel, PowerPoint, Text, CSV, Images (JPG, PNG, GIF, WebP)
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AttachmentManager;
